import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Suspense, lazy } from 'react'
const Layout = lazy(() => import('@/components/layout/Layout'))
import { AuthProvider } from '@/components/auth'
import { ToastProvider } from '@/components/ui'
import { ErrorBoundary } from '@/components/error/ErrorBoundary'
import { ThemeProvider } from '@/contexts/ThemeContext'
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const EpisodesPage = lazy(() => import('@/pages/EpisodesPage'))
const EpisodeDetailPage = lazy(() => import('@/pages/EpisodeDetailPage'))
const AssetDatabasePage = lazy(() => import('@/pages/AssetDatabasePage'))
const CalendarPage = lazy(() => import('@/pages/CalendarPage'))
const SocialCalendarPage = lazy(() => import('@/pages/SocialCalendarPage'))
const SocialAnalyticsPage = lazy(() => import('@/pages/SocialAnalyticsPage'))
const SocialPostPage = lazy(() => import('@/pages/SocialPostPage'))
const PodcastCalendarPage = lazy(() => import('@/pages/PodcastCalendarPage'))
const GuestsPage = lazy(() => import('@/pages/GuestsPage'))
const GuestDetailPage = lazy(() => import('@/pages/GuestDetailPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const LateConnectCallbackPage = lazy(() => import('@/pages/LateConnectCallbackPage'))

// Lazy load public pages (no auth/layout needed)
const GuestBrandKitPage = lazy(() => import('@/pages/GuestBrandKitPage'))
const UserGuidePage = lazy(() => import('@/pages/UserGuidePage'))
const SharedPostsPage = lazy(() => import('@/pages/SharedPostsPage'))
const PublicGuestsPage = lazy(() => import('@/pages/PublicGuestsPage'))
const PublicLayout = lazy(() => import('@/components/layout/PublicLayout'))
const DesignLabPage = lazy(() => import('@/pages/DesignLabPage'))
const GalleryPage = lazy(() => import('@/pages/GalleryPage'))
const GalleryIndexPage = lazy(() => import('@/pages/GalleryIndexPage'))

// Loading spinner for suspense
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-[var(--bg-void)]">
    <div className="spinner" />
  </div>
)

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <BrowserRouter>
          <Suspense fallback={<LoadingSpinner />}><Routes>
            {/* Public pages (no auth, no layout) */}
            <Route path="/share/:token" element={
              <Suspense fallback={<LoadingSpinner />}>
                <GuestBrandKitPage />
              </Suspense>
            } />
            <Route path="/share/:token/posts" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SharedPostsPage />
              </Suspense>
            } />
            <Route path="/gallery/:uuid" element={
              <Suspense fallback={<LoadingSpinner />}>
                <GalleryPage />
              </Suspense>
            } />
            <Route path="/guide" element={
              <Suspense fallback={<LoadingSpinner />}>
                <UserGuidePage />
              </Suspense>
            } />

            {/* Late OAuth callback (used for connecting social accounts) */}
            <Route path="/late/connect/callback" element={<LateConnectCallbackPage />} />

            {/* Design Lab - temporary route for CTA design review */}
            <Route path="/__design_lab" element={
              <Suspense fallback={<LoadingSpinner />}>
                <DesignLabPage />
              </Suspense>
            } />
            
            {/* Public calendar and guest directory (with PublicLayout) */}
            <Route path="/public/podcast-calendar" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PublicLayout>
                  <PodcastCalendarPage isPublic />
                </PublicLayout>
              </Suspense>
            } />
            <Route path="/guests/directory" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PublicLayout>
                  <PublicGuestsPage />
                </PublicLayout>
              </Suspense>
            } />
            
            {/* Auth-wrapped routes */}
            <Route path="/*" element={
              <AuthProvider>
                <Routes>
                  {/* Auth pages (public but within auth context) */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  
                  {/* Protected app routes with layout */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<EpisodesPage />} />
                    <Route path="guests" element={<GuestsPage />} />
                    <Route path="guests/:id" element={<GuestDetailPage />} />
                    <Route path="assets" element={<AssetDatabasePage />} />
                    <Route path="podcast-calendar" element={<PodcastCalendarPage />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="social/calendar" element={<SocialCalendarPage />} />
                    <Route path="social/analytics" element={<SocialAnalyticsPage />} />
                    <Route path="social/post/:id" element={<SocialPostPage />} />
                    <Route path="episode/:id" element={<EpisodeDetailPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="galleries" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <GalleryIndexPage />
                      </Suspense>
                    } />
                  </Route>
                </Routes>
              </AuthProvider>
            } />
          </Routes></Suspense>
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>
  </ErrorBoundary>
)
}

export default App
